import { c as _c } from "react/compiler-runtime";
import type { MouseEvent } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@repo/blacksmith-ui/button";
import { cn } from "@repo/blacksmith-ui/cn";
import { STATUS_COLORS } from "@/components/filters/StatusFilter";
export const KEEP_GOING_ROUNDS = 3;
interface LoopLimitStripProps {
  headline: string;
  detail?: string;
  onKeepGoing?: (event: MouseEvent<HTMLButtonElement>) => void | Promise<void>;
  actionLabel?: string;
  pending: boolean;
  continued: boolean;
  continuedHeadline: string;
  continuedDetail?: string;
  testId: string;
  tabIndex?: number;
  className?: string;
  onClick?: (event: MouseEvent<HTMLDivElement>) => void;
  /** One 32px line with a pill action; the detail line is not rendered. */
  compact?: boolean;
}
export function LoopLimitStrip(t0) {
  const $ = _c(40);
  const {
    headline,
    detail,
    onKeepGoing,
    actionLabel: t1,
    pending,
    continued,
    continuedHeadline,
    continuedDetail,
    testId,
    tabIndex,
    className,
    onClick,
    compact: t2
  } = t0;
  const actionLabel = t1 === undefined ? "Keep going" : t1;
  const compact = t2 === undefined ? false : t2;
  const displayedHeadline = continued ? continuedHeadline : headline;
  const displayedDetail = continued ? continuedDetail : detail;
  const t3 = compact ? "h-8 gap-2 pl-2.5 pr-1" : "flex-wrap gap-x-3 gap-y-1.5 px-3 py-2";
  let t4;
  if ($[0] !== className || $[1] !== t3) {
    t4 = cn("flex items-center justify-between rounded-md border border-[var(--color-border)] bg-[var(--color-surface)]", t3, className);
    $[0] = className;
    $[1] = t3;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const t5 = compact ? "items-center" : "items-start";
  let t6;
  if ($[3] !== t5) {
    t6 = cn("flex min-w-0 gap-2", t5);
    $[3] = t5;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const t7 = !compact && "mt-[7px]";
  let t8;
  if ($[5] !== t7) {
    t8 = cn("size-1.5 shrink-0 rounded-full", t7);
    $[5] = t7;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  const t9 = continued ? "var(--color-border)" : STATUS_COLORS.queued;
  let t10;
  if ($[7] !== t9) {
    t10 = {
      backgroundColor: t9
    };
    $[7] = t9;
    $[8] = t10;
  } else {
    t10 = $[8];
  }
  let t11;
  if ($[9] !== t10 || $[10] !== t8) {
    t11 = <span aria-hidden={true} className={t8} style={t10} />;
    $[9] = t10;
    $[10] = t8;
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  const t12 = compact ? "truncate type-body-xs-medium" : "type-body-s-medium";
  let t13;
  if ($[12] !== t12) {
    t13 = cn("text-neutral-900 dark:text-neutral-100", t12);
    $[12] = t12;
    $[13] = t13;
  } else {
    t13 = $[13];
  }
  let t14;
  if ($[14] !== displayedHeadline || $[15] !== t13) {
    t14 = <p className={t13}>{displayedHeadline}</p>;
    $[14] = displayedHeadline;
    $[15] = t13;
    $[16] = t14;
  } else {
    t14 = $[16];
  }
  let t15;
  if ($[17] !== compact || $[18] !== displayedDetail) {
    t15 = displayedDetail && !compact ? <p className="type-body-xs-regular text-neutral-500 dark:text-neutral-400">{displayedDetail}</p> : null;
    $[17] = compact;
    $[18] = displayedDetail;
    $[19] = t15;
  } else {
    t15 = $[19];
  }
  let t16;
  if ($[20] !== t14 || $[21] !== t15) {
    t16 = <div className="min-w-0">{t14}{t15}</div>;
    $[20] = t14;
    $[21] = t15;
    $[22] = t16;
  } else {
    t16 = $[22];
  }
  let t17;
  if ($[23] !== t11 || $[24] !== t16 || $[25] !== t6) {
    t17 = <div className={t6}>{t11}{t16}</div>;
    $[23] = t11;
    $[24] = t16;
    $[25] = t6;
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  let t18;
  if ($[27] !== actionLabel || $[28] !== compact || $[29] !== continued || $[30] !== onKeepGoing || $[31] !== pending || $[32] !== tabIndex) {
    t18 = !continued && onKeepGoing ? <Button type="button" disabled={pending} onClick={onKeepGoing} tabIndex={tabIndex} variant="secondary" size={compact ? "xs" : undefined} className={compact ? "shrink-0" : undefined}>{pending ? <Loader2 className="mr-1 size-3 animate-spin" /> : null}{actionLabel}</Button> : null;
    $[27] = actionLabel;
    $[28] = compact;
    $[29] = continued;
    $[30] = onKeepGoing;
    $[31] = pending;
    $[32] = tabIndex;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] !== onClick || $[35] !== t17 || $[36] !== t18 || $[37] !== t4 || $[38] !== testId) {
    t19 = <div className={t4} data-testid={testId} onClick={onClick}>{t17}{t18}</div>;
    $[34] = onClick;
    $[35] = t17;
    $[36] = t18;
    $[37] = t4;
    $[38] = testId;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  return t19;
}